import { Add, Close, Person } from "@mui/icons-material";
import { Alert, Box, Button, capitalize, Divider, IconButton, Modal, Skeleton, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Table, usePagination } from "@frontend/features/common/components/Table";
import { ModalBox } from "@frontend/features/common/utils/modals";
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';

import { CreateClientModal } from "../CreateClient";
import { useGetClientsByAgency } from "../../queries";

export interface ClientSelectorTableItem {
  name: string,
  id: string,
  agency: string,
  phone: string,
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (clientTableItem: ClientSelectorTableItem) => void;
  agencyId: number;
  selectedItemId?: number | string;
}

export const ClientSelectorModal = ({ open, onClose, onSelect, agencyId, selectedItemId }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [createClientmodal, setCreateClientModal] = useState<boolean>(false);
  const pagination = usePagination(undefined, undefined, { keepPaginationInUrl: false });

  const toggleCreate = () => setCreateClientModal(prev => !prev);

  const handleOnSearch = (value: string) => {
    setSearch(value.trim().replace(/\s+/g, ' '));
    pagination.handlePagination(1, false);
  };

  const { data: clients, isFetching, isError, refetch } = useGetClientsByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    agencyId: agencyId.toString(),
  });

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="client-modal-selector-title">
      <ModalBox display="flex" flexDirection="column" gap={theme.spacing(2)} sx={{ width: 800, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box', overflow: 'auto' }}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Person color="disabled" />
            <Typography id="client-modal-selector-title" variant="h6" component="h2">
              {t('clients.selector.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider />


        <Typography variant="body2" color="text.secondary">
          {t('clients.selector.searchHint')}
        </Typography>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
          <SearchInput placeholder={t('clients.selector.searchPlaceholder')} onSearch={handleOnSearch} />
          <Button variant="outlined" startIcon={<Add />} onClick={toggleCreate} sx={{ flexShrink: 0 }}>
            {t('clients.create.title')}
          </Button>
        </Box>
        <Typography variant="body2" aria-live="polite">
          {isFetching ? t('clients.selector.loading') : `${clients?.count ?? 0} ${t('clients.results')}`}
        </Typography>
        {isError && <Alert severity="error" action={<Button onClick={() => refetch()}>{t('clients.selector.retry')}</Button>}>
          {t('clients.selector.error')}
        </Alert>}
        {!isFetching && !isError && clients?.count === 0 && <Alert severity="info">{t('clients.selector.empty')}</Alert>}
        <CreateClientModal open={createClientmodal} onClose={toggleCreate} />

        {isFetching && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}

        {!isFetching && !isError && clients && clients.count > 0 && <Table
          selectedItemId={selectedItemId}
          onRowSelect={onSelect}
          columns={[
            {
              id: 'name',
              label: t('clients.table.name'),
              align: 'left',
            },
            {
              id: 'agency',
              label: t('clients.table.agency'),
              align: 'left',
            },
            { id: 'phone', label: t('clients.table.phone'), align: 'left' },
            { id: 'email', label: t('clients.table.email'), align: 'left' },
          ]}
          data={{
            key: 'id',
            items: clients?.items.map((client) => ({
              name: `${capitalize(String(client.firstName))} ${capitalize(String(client.lastName))}`,
              id: client.id,
              agency: client.agencyName,
              phone: `(${client.phoneCountryCode}) ${client.phoneNumber}`,
              email: client.email,
            })),
            totalCount: clients.count,
          }}
          pagination={pagination}
          stickyHeader
        />}

      </ModalBox>
    </Modal>
  );
};
